import styles from "./StatusChip.module.css";

/**
 * StatusChip — the four transaction statuses.
 *
 * Design principle: the CHIP carries the signal. Amounts beside a
 * failed or reversed row render `--text-tertiary`, never `--danger`.
 * `--danger` only appears here, and only on the "Failed" chip.
 *
 * Reversed vs. Failed are the two lookalikes users are most likely
 * to confuse — they get DIFFERENT colours AND a small glyph so a
 * quick scan of a long list never conflates "money returned to
 * sender" with "the transfer failed and no money moved".
 *
 *   Pending    · amber wash                — action pending
 *   Completed  · neutral surface           — settled, no attention needed
 *   Failed     · red text on red wash      — no money moved; retry surface
 *   Reversed   · slate wash + ↩ glyph      — money moved and moved back
 */

export type TransactionStatus =
  | "pending"
  | "completed"
  | "failed"
  | "reversed";

interface StatusChipProps {
  status: TransactionStatus;
}

const LABEL: Record<TransactionStatus, string> = {
  pending: "Pending",
  completed: "Completed",
  failed: "Failed",
  reversed: "Reversed",
};

// Inline SVG kept tiny — no icon-lib dependency for one glyph.
function ReversedGlyph() {
  return (
    <svg
      viewBox="0 0 16 16"
      width={11}
      height={11}
      aria-hidden
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 8h6a3 3 0 0 1 0 6H4" />
      <path d="M7 5 4 8l3 3" />
    </svg>
  );
}

export function StatusChip({ status }: StatusChipProps) {
  const classes = [styles.chip, styles[status]]
    .filter(Boolean)
    .join(" ");
  return (
    <span className={classes} role="status">
      {status === "reversed" ? (
        <span className={styles.icon}>
          <ReversedGlyph />
        </span>
      ) : null}
      {LABEL[status]}
    </span>
  );
}
